import { Skeleton } from '@mui/material';
import { ReactNode, useEffect } from 'react';
import { useOutletContext, useNavigate, Outlet } from 'react-router';

export interface OutletContextReliantRouteProps<T> {
  getOutletContextState: (outletContext: T) => 'ready' | 'pending' | 'error';
  errorRedirectionPath?: string;
  renderPendingContent?: (outletContext: T) => ReactNode;
  renderErrorContent?: (outletContext: T) => ReactNode;
}

/**
 * This wrapper component is meant for sub pages so there is no extra execution or elements rendered
 * until the "OutletContext" they depend on has been verified, which usually means that some data has
 * been loaded, proving its existence.
 */
const OutletContextReliantRoute = <T,>({
  getOutletContextState,
  errorRedirectionPath,
  renderErrorContent,
  renderPendingContent,
}: OutletContextReliantRouteProps<T>) => {
  const context = useOutletContext<T>();
  const navigate = useNavigate();

  const contextState = getOutletContextState(context);

  useEffect(() => {
    if (contextState === 'error' && errorRedirectionPath) {
      void navigate(errorRedirectionPath);
    }
  }, [contextState, navigate, errorRedirectionPath]);

  if (contextState === 'ready') {
    return <Outlet context={context} />;
  }

  const defaultContent = (
    <div className="grid grid-cols-[1fr] grid-rows-[1fr] h-full">
      <Skeleton height="100%" className="block" variant="rounded" />
    </div>
  );

  if (contextState === 'pending') {
    return renderPendingContent
      ? renderPendingContent(context)
      : defaultContent;
  } else {
    return renderErrorContent
      ? renderErrorContent(context)
      : renderPendingContent
        ? renderPendingContent(context)
        : defaultContent;
  }
};

export default OutletContextReliantRoute;
